import type { Capture, GameState, Move, Piece, Side, VictoryResult } from "./types";
import { cloneState } from "./setup";
import { squareKey, sameSquare } from "./coords";
import { hasAnyLegalMove, validateMove } from "./moves";
import { findAssaults, findEncounters, findSieges } from "./captures";
import { checkVictory } from "./victory";

export interface PendingTurn {
  /** State after the piece was moved and any auto-Sieges have resolved. Turn has NOT yet switched. */
  state: GameState;
  /** The move just applied. Stored for finalization. */
  move: Move;
  /** Side that made the move. */
  side: Side;
  /** Sieges automatically resolved by this move. */
  sieges: Capture[];
  /** Encounter / Assault / Deceit captures that the player MAY declare to resolve. */
  declarable: Capture[];
}

/**
 * Apply a move and auto-resolve any Sieges that result.
 * Returns the post-move state plus the list of optional captures the player can declare.
 * Throws on invalid move.
 */
export function applyMove(state: GameState, move: Move): PendingTurn {
  const err = validateMove(state, move);
  if (err) throw new Error(`invalid move: ${err}`);

  const next = cloneState(state);
  const fromKey = squareKey(move.from);
  const toKey = squareKey(move.to);

  const piece = next.board.get(fromKey);
  if (!piece) throw new Error("invalid move: source piece vanished (shouldn't happen)");
  next.board.delete(fromKey);
  next.board.set(toKey, piece);

  const side = piece.side;

  // Auto-resolve Sieges. A move can complete multiple sieges in one shot (rare but possible).
  // Per Fulke V1: Oblivion takes the whole King outright — one Siege = one event in
  // history.captures (no pyramidLayer field), but captured[] gets per-layer entries for HUD
  // accounting. The asymmetry is intentional: history.captures records what the *player did*
  // (one Siege event), while captured[] tracks what's in the captor's pile.
  const sieges = findSieges(next, side);
  for (const siege of sieges) {
    const targetKey = squareKey(siege.target);
    const captured = next.board.get(targetKey);
    if (!captured) continue;
    if (captured.shape === "pyramid" && captured.layers) {
      for (let i = 0; i < captured.layers.length; i++) {
        const layer = captured.layers[i];
        if (layer.captured) continue;
        next.captured[side].push({
          id: `${captured.id}-layer-${i}`,
          side: captured.side,
          shape: layer.shape,
          value: layer.value,
        });
      }
    } else {
      next.captured[side].push(captured);
    }
    next.board.delete(targetKey);
  }

  // Find declarable Encounter and Assault captures (Deceit is folded into Encounter for v1).
  const declarable: Capture[] = [
    ...findEncounters(next, side),
    ...findAssaults(next, side),
  ];

  return { state: next, move, side, sieges, declarable };
}

/**
 * Finalize the turn: apply an optional declared capture (Encounter/Assault/Deceit), record history,
 * check victory, and switch sides.
 */
export function finalizeTurn(
  pending: PendingTurn,
  declared?: Capture,
  options: { advance?: boolean } = {},
): { state: GameState; victory: VictoryResult | null } {
  const next = cloneState(pending.state);
  const side = pending.side;
  const allCaptures: Capture[] = [...pending.sieges];

  if (declared) {
    // Validate that the declared capture is still applicable in the current state.
    const targetKey = squareKey(declared.target);
    const target = next.board.get(targetKey);
    if (!target) {
      throw new Error("invalid declared capture: target square is empty");
    }
    if (target.side === side) {
      throw new Error("invalid declared capture: target is friendly");
    }
    // Verify that all attacker squares still hold our pieces (basic sanity check).
    for (const aSq of declared.attackers) {
      const aPiece = next.board.get(squareKey(aSq));
      if (!aPiece || aPiece.side !== side) {
        throw new Error("invalid declared capture: attacker missing or not friendly");
      }
    }

    // Layer capture vs whole-piece capture.
    if (declared.pyramidLayer && target.shape === "pyramid" && target.layers) {
      const li = declared.pyramidLayer.layerIndex;
      const layer = target.layers[li];
      if (!layer || layer.captured) {
        throw new Error("invalid declared capture: pyramid layer already captured");
      }
      // Flip the layer; record the synthetic captured piece for the captured pile / UI.
      const updatedLayers = target.layers.map((l, idx) =>
        idx === li ? { ...l, captured: true } : l,
      );
      const newValue = updatedLayers.reduce((acc, l) => (l.captured ? acc : acc + l.value), 0);
      const allCapturedNow = updatedLayers.every((l) => l.captured);

      next.captured[side].push({
        id: `${target.id}-layer-${li}`,
        side: target.side,
        shape: layer.shape,
        value: layer.value,
      });

      if (allCapturedNow) {
        next.board.delete(targetKey);
      } else {
        next.board.set(targetKey, { ...target, layers: updatedLayers, value: newValue });
      }
      // Advance is not offered on layer captures — Pyramid stays put.
    } else {
      // Standard whole-piece capture.
      next.captured[side].push(target);
      next.board.delete(targetKey);

      // Optional advance for Encounter/Deceit (single attacker only).
      if (
        options.advance &&
        (declared.method === "encounter" || declared.method === "deceit") &&
        declared.attackers.length === 1
      ) {
        const aSq = declared.attackers[0];
        const aKey = squareKey(aSq);
        const attacker = next.board.get(aKey);
        if (attacker && !sameSquare(aSq, declared.target)) {
          next.board.delete(aKey);
          next.board.set(targetKey, attacker);
        }
      }
    }

    allCaptures.push(declared);
  }

  next.history.push({ side, move: pending.move, captures: allCaptures });
  next.turnNumber += 1;

  let victory = checkVictory(next, side);
  if (victory) {
    next.victory = victory;
  }

  next.toMove = side === "white" ? "black" : "white";

  // Stalemate: if the next side has no legal moves and the game hasn't already ended, the
  // stalemated side loses. The historical rules forbid passing, so we treat it as a loss.
  if (!next.victory && !hasAnyLegalMove(next, next.toMove)) {
    const winner: Side = next.toMove === "white" ? "black" : "white";
    next.victory = {
      level: next.victoryConfig.level,
      side: winner,
      detail: `${next.toMove} has no legal moves — ${winner} wins by stalemate`,
    };
    victory = next.victory;
  }

  return { state: next, victory };
}

/** One-shot helper: apply move + finalize with chosen options. Useful for bots and tests. */
export function applyTurn(
  state: GameState,
  move: Move,
  options: { declared?: Capture; advance?: boolean } = {},
): { state: GameState; pending: PendingTurn; victory: VictoryResult | null } {
  const pending = applyMove(state, move);
  const { state: finalState, victory } = finalizeTurn(pending, options.declared, { advance: options.advance ?? false });
  return { state: finalState, pending, victory };
}

/** Convenience: list pieces of a given side currently on the board. */
export function piecesOf(state: GameState, side: Side): Array<{ piece: Piece; key: string }> {
  const out: Array<{ piece: Piece; key: string }> = [];
  for (const [key, piece] of state.board) {
    if (piece.side === side) out.push({ piece, key });
  }
  return out;
}
